import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { Header } from '@components/Header';
import TrashBin from '@components/icons/TrashBin.svg';
import { PagePagination } from '@components/core-ui/Pagination';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { hasVsDeveloperRole } from '@helpers/hasVsDeveloperRole';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation } from '@tanstack/react-query';
import { AdminWorkspaceListItemWithOrganizationResponseDto, WorkspacesService } from 'app/API';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import toast from 'react-hot-toast';
import { usePageContext } from '../../providers/PageProvider';
import { CellContent } from './styles/CellContent';
import { StyledLink } from './styles/StyledLink';
import { Wrapper } from './styles/Wrapper';

export const Content = () => {
  const { me } = useAuthContext();
  const { call } = useAPI();
  const { openModal, closeModal } = useModalContext();
  const {
    workspaces,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    setPageSize,
    isLoading,
    hasInitialError,
    retryWorkspaces,
    exportCSV,
    isExporting,
    reloadPage,
  } = usePageContext();
  const canDeleteWorkspaces = hasVsDeveloperRole(me?.roles);

  const deleteWorkspaceMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      call(() => WorkspacesService.deleteWs({ wsid: workspaceId }), {
        onError: (message) => toast.error(message || 'Unable to delete workspace'),
      }),
    onSuccess: (response) => {
      if (!response.success) {
        toast.error(response.error ?? 'Unable to delete workspace');
        return;
      }
      toast.success('Workspace marked as deleted');
      reloadPage();
    },
  });

  const openDeleteWorkspaceModal = (workspace: AdminWorkspaceListItemWithOrganizationResponseDto) => {
    openModal(ModalNamesEnum.CustomConfirmationModal, {
      title: 'Delete workspace',
      description: `This will mark ${workspace.name} as deleted.`,
      buttonText: 'Delete',
      onCancel: closeModal,
      onSuccess: () => {
        closeModal();
        deleteWorkspaceMutation.mutate(workspace.id);
      },
    });
  };

  return (
    <>
      <Header
        title={'Workspaces'}
        buttonLabel={workspaces.data.length ? 'Export CSV' : undefined}
        buttonOnClick={workspaces.data.length ? exportCSV : undefined}
        buttonLoading={isExporting}
        setSearch={setSearch}
        search={search}
      />

      <LinearProgressLoader active={isLoading || deleteWorkspaceMutation.isPending} />
      <Wrapper>
        {hasInitialError ? (
          <PageErrorState
            message="Couldn't load workspaces"
            actionLabel="Retry"
            actionDisabled={isLoading}
            onAction={retryWorkspaces}
          />
        ) : (
          <>
            <TableWrapper>
              <Table>
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th>Name</Th>
                    <Th>URL</Th>
                    <Th>Organization</Th>
                    {canDeleteWorkspaces ? <Th>Actions</Th> : null}
                  </Tr>
                </Thead>
                <Tbody>
                  {workspaces.data.map((data, index) => (
                    <Tr key={data.id}>
                      <Td>
                        <CellContent>{index + 1}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <StyledLink to={routes.adminWorkspaceDetails.make(data.id)}>{data.name}</StyledLink>
                        </CellContent>
                      </Td>
                      <Td>
                        <CellContent>{data.slug}</CellContent>
                      </Td>
                      <Td>
                        <CellContent>
                          <StyledLink to={routes.adminOrganizationDetails.make(data.Organization.id)}>
                            {data.Organization.name}
                          </StyledLink>
                        </CellContent>
                      </Td>
                      {canDeleteWorkspaces ? (
                        <Td>
                          <IconButton
                            title="Delete workspace"
                            disabled={deleteWorkspaceMutation.isPending || isLoading}
                            onClick={() => openDeleteWorkspaceModal(data)}
                          >
                            <TrashBin />
                          </IconButton>
                        </Td>
                      ) : null}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableWrapper>
            <PagePagination
              totalItemsCount={workspaces.count}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
            />
          </>
        )}
      </Wrapper>
    </>
  );
};
